// AI モデルの一覧と、前処理 / 後処理の純ロジック (Node テスト対象)。
// モデル本体と onnxruntime-web はアプリに同梱せず、ユーザーの明示的な同意のあとで取得する (docs/PLAN.md Phase 5)。

export interface AiModelDef {
  id: string
  name: string
  /** 取得元 (Hugging Face) */
  url: string
  /** おおよそのバイト数 (同意画面に表示) */
  bytes: number
  license: string
  licenseUrl: string
  /** 入力サイズ: fixed = 固定、shortest = 短辺を合わせて 32 の倍数に丸める */
  input: { kind: 'fixed'; width: number; height: number } | { kind: 'shortest'; edge: number; divisor: number; maxEdge: number }
  mean: [number, number, number]
  std: [number, number, number]
  /** 出力を min-max で正規化する (RMBG-1.4) */
  normalizeOutput: boolean
  note: string
}

export const ORT_VERSION = '1.29.0'
export const ORT_BASE = `https://cdn.jsdelivr.net/npm/onnxruntime-web@${ORT_VERSION}/dist/`
export const ORT_MODULE_URL = `${ORT_BASE}ort.wasm.min.mjs`
export const ORT_WASM_URL = `${ORT_BASE}ort-wasm-simd-threaded.wasm`
export const ORT_WASM_BYTES = 13_961_845

export const AI_MODELS: AiModelDef[] = [
  {
    id: 'modnet-q',
    name: 'MODNet (量子化, 6.6 MB)',
    url: 'https://huggingface.co/Xenova/modnet/resolve/main/onnx/model_quantized.onnx',
    bytes: 6_632_188,
    license: 'Apache-2.0',
    licenseUrl: 'https://huggingface.co/Xenova/modnet',
    input: { kind: 'shortest', edge: 512, divisor: 32, maxEdge: 1024 },
    mean: [0.5, 0.5, 0.5],
    std: [0.5, 0.5, 0.5],
    normalizeOutput: false,
    note: '人物向けのマッティング。軽量'
  },
  {
    id: 'modnet',
    name: 'MODNet (fp32, 25.9 MB)',
    url: 'https://huggingface.co/Xenova/modnet/resolve/main/onnx/model.onnx',
    bytes: 25_888_640,
    license: 'Apache-2.0',
    licenseUrl: 'https://huggingface.co/Xenova/modnet',
    input: { kind: 'shortest', edge: 512, divisor: 32, maxEdge: 1024 },
    mean: [0.5, 0.5, 0.5],
    std: [0.5, 0.5, 0.5],
    normalizeOutput: false,
    note: '人物向けのマッティング。高精度'
  },
  {
    id: 'rmbg-1.4-q',
    name: 'RMBG-1.4 (量子化, 44 MB)',
    url: 'https://huggingface.co/briaai/RMBG-1.4/resolve/main/onnx/model_quantized.onnx',
    bytes: 44_403_226,
    license: 'CC BY-NC 4.0 (非商用のみ)',
    licenseUrl: 'https://huggingface.co/briaai/RMBG-1.4',
    input: { kind: 'fixed', width: 1024, height: 1024 },
    mean: [0.5, 0.5, 0.5],
    std: [1, 1, 1],
    normalizeOutput: true,
    note: '人物以外 (物体・動物など) にも対応。ライセンスに注意'
  }
]

export function getAiModel(id: string): AiModelDef | undefined {
  return AI_MODELS.find(m => m.id === id)
}

/** モデルに入れる解像度を決める */
export function inputSize(def: AiModelDef, width: number, height: number): { w: number; h: number } {
  if (def.input.kind === 'fixed') return { w: def.input.width, h: def.input.height }
  const { edge, divisor, maxEdge } = def.input
  const short = Math.min(width, height)
  let s = edge / short
  // 長辺が上限を超えないように
  if (Math.max(width, height) * s > maxEdge) s = maxEdge / Math.max(width, height)
  const round = (v: number) => Math.max(divisor, Math.round((v * s) / divisor) * divisor)
  return { w: round(width), h: round(height) }
}

/** RGBA → バイリニア縮小 → CHW float32 (正規化)。透明部分は白として扱う */
export function preprocess(rgba: Uint8ClampedArray, width: number, height: number, def: AiModelDef): { data: Float32Array; w: number; h: number } {
  const { w, h } = inputSize(def, width, height)
  const data = new Float32Array(3 * w * h)
  const sx = width / w, sy = height / h
  for (let y = 0; y < h; y++) {
    const fy = Math.min(height - 1.001, (y + 0.5) * sy - 0.5)
    const y0 = Math.max(0, Math.floor(fy)), ty = Math.max(0, fy - y0), y1 = Math.min(height - 1, y0 + 1)
    for (let x = 0; x < w; x++) {
      const fx = Math.min(width - 1.001, (x + 0.5) * sx - 0.5)
      const x0 = Math.max(0, Math.floor(fx)), tx = Math.max(0, fx - x0), x1 = Math.min(width - 1, x0 + 1)
      const i00 = (y0 * width + x0) * 4, i10 = (y0 * width + x1) * 4, i01 = (y1 * width + x0) * 4, i11 = (y1 * width + x1) * 4
      for (let c = 0; c < 3; c++) {
        // 透明部分は白 (255) に合成してから読む
        const v00 = rgba[i00 + c]! * (rgba[i00 + 3]! / 255) + 255 * (1 - rgba[i00 + 3]! / 255)
        const v10 = rgba[i10 + c]! * (rgba[i10 + 3]! / 255) + 255 * (1 - rgba[i10 + 3]! / 255)
        const v01 = rgba[i01 + c]! * (rgba[i01 + 3]! / 255) + 255 * (1 - rgba[i01 + 3]! / 255)
        const v11 = rgba[i11 + c]! * (rgba[i11 + 3]! / 255) + 255 * (1 - rgba[i11 + 3]! / 255)
        const v = (v00 * (1 - tx) + v10 * tx) * (1 - ty) + (v01 * (1 - tx) + v11 * tx) * ty
        data[c * w * h + y * w + x] = (v / 255 - def.mean[c]!) / def.std[c]!
      }
    }
  }
  return { data, w, h }
}

/** モデル出力 (w×h の 0..1) を文書解像度の 0..255 マスクへ (バイリニア拡大) */
export function postprocess(matte: Float32Array, w: number, h: number, width: number, height: number, def: AiModelDef): Uint8ClampedArray {
  let lo = 0, hi = 1
  if (def.normalizeOutput) {
    lo = Infinity; hi = -Infinity
    for (let i = 0; i < matte.length; i++) {
      const v = matte[i]!
      if (v < lo) lo = v
      if (v > hi) hi = v
    }
    if (!(hi > lo)) { lo = 0; hi = 1 }
  }
  const out = new Uint8ClampedArray(width * height)
  const sx = w / width, sy = h / height
  for (let y = 0; y < height; y++) {
    const fy = Math.min(h - 1.001, (y + 0.5) * sy - 0.5)
    const y0 = Math.max(0, Math.floor(fy)), ty = Math.max(0, fy - y0), y1 = Math.min(h - 1, y0 + 1)
    for (let x = 0; x < width; x++) {
      const fx = Math.min(w - 1.001, (x + 0.5) * sx - 0.5)
      const x0 = Math.max(0, Math.floor(fx)), tx = Math.max(0, fx - x0), x1 = Math.min(w - 1, x0 + 1)
      const v = (matte[y0 * w + x0]! * (1 - tx) + matte[y0 * w + x1]! * tx) * (1 - ty) + (matte[y1 * w + x0]! * (1 - tx) + matte[y1 * w + x1]! * tx) * ty
      out[y * width + x] = Math.round(Math.max(0, Math.min(1, (v - lo) / (hi - lo))) * 255)
    }
  }
  return out
}

/** 同意の内容 (localStorage に保存)。バージョンを上げると再同意が必要 */
export interface AiConsent {
  version: 1
  agreedAt: number
  modelId: string
}
export const CONSENT_KEY = 'bpe.ai.consent.v1'

export function parseConsent(raw: string | null): AiConsent | null {
  if (!raw) return null
  try {
    const v = JSON.parse(raw) as Partial<AiConsent>
    if (v && v.version === 1 && typeof v.agreedAt === 'number' && typeof v.modelId === 'string' && getAiModel(v.modelId)) return v as AiConsent
  } catch {
    /* ignore */
  }
  return null
}
